import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, MenuItem } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import FormProvider, { RHFTextField, RHFSelect, RHFSwitch } from 'components/hook-form';
import { useSelector } from 'redux/store';
import { IPack, IPackCreate } from '../../../../@types/package';
import RHFJSONEditor from 'components/hook-form/RHFJSONEditor';
import { useCallback, useEffect, useState } from 'react';
import server, { getFullServers, getPacksWithServer } from 'redux/slices/server';
import { getFullServices } from 'redux/slices/service';
import { convertStringToArray } from 'utils/func';
import { countries } from 'assets/data';
import { IServer, IServerPack } from '../../../../@types/server';
import PackProviderPopover from './PackProviderPopover';
import { TypeService } from 'utils/constant';

// ----------------------------------------------------------------------
type Props = {
  onSubmit: (payload: IPackCreate) => void;
  currentData: IPack;
  isUpdate: boolean;
};
type FormValuesProps = {
  name: string;
  displayname: string;
  type: string;
  pricesale: number;
  pricebuy: number;
  quantitymin: number;
  quantitymax: number;
  discount: number;
  maxdiscount: number;
  server: string;
  isuid: boolean;
  service: string;
  template: string;
  fields: string;
  items: any;
  serviceprovider: string;
  actions: string;
  status: number;
};

const PackStatus = [
  { value: 0, name: 'Bảo trì' },
  { value: 1, name: 'Hoạt động' },
  { value: 2, name: 'Ẩn khỏi hệ thống' },
];

export default function AuthLoginForm({ onSubmit, currentData, isUpdate }: Props) {
  const { fullServices } = useSelector((state) => state.service);
  const { fullServers } = useSelector((state) => state.server);
  const { user } = useSelector((state) => state.global);
  const [packs, setPacks] = useState<IServerPack[]>([]);
  const actions = (user?.permission && user?.permission['orderseeding']) ?? [];

  useEffect(() => {
    if (actions.length > 0 && (fullServers.length < 1 || fullServices.length < 1)) {
      getFullServers();
      getFullServices();
    }
  }, []);

  const loadPacksWithServer = useCallback(async (serverId: string) => {
    const data = await getPacksWithServer(serverId);
    setPacks(data);
  }, []);

  const BankSchema = Yup.object().shape({
    name: Yup.string().required('Thông tin bắt buộc'),
    server: Yup.string().required('Thông tin bắt buộc'),
    type: Yup.string().required('Thông tin bắt buộc'),
    displayname: Yup.string().required('Thông tin bắt buộc'),
    service: Yup.string().required('Thông tin bắt buộc'),
    pricesale: Yup.number().required('Thông tin bắt buộc'),
    pricebuy: Yup.number().required('Thông tin bắt buộc'),
    discount: Yup.number().min(0, 'Tối thiểu là 0').max(15, 'Tối đa là 15%'),
    maxdiscount: Yup.number().min(0, 'Tối thiểu là 0'),
    isuid: Yup.boolean(),
    quantitymin: Yup.number(),
    quantitymax: Yup.number(),
    status: Yup.number(),
    flag: Yup.string(),
  });

  const defaultValues = {
    server: currentData?.server.id ?? '',
    service: currentData?.service.id ?? '',
    name: currentData?.name ?? '',
    displayname: currentData?.displayname ?? '',
    type: currentData?.type ?? '',
    pricesale: currentData?.pricesale ?? 0,
    pricebuy: currentData?.pricebuy ?? 0,
    template: currentData?.template ?? {},
    fields: currentData?.fields?.join('\n') ?? '',
    actions: currentData?.actions?.join('\n') ?? '',
    isuid: currentData?.isuid ?? false,
    discount: currentData?.discount ?? 0,
    maxdiscount: currentData?.maxdiscount ?? 0,
    items: currentData?.items ?? [],
    serviceprovider: currentData?.serviceprovider ?? '',
    quantitymin: currentData?.quantitymin ?? 0,
    quantitymax: currentData?.quantitymax ?? 0,
    status: currentData?.status ?? 0,
    flag: currentData?.flag ?? '',
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(BankSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, isSubmitSuccessful },
  } = methods;

  const onSubmitForm = async (data: FormValuesProps) => {
    onSubmit({
      ...data,
      actions: convertStringToArray(data.actions),
      fields: convertStringToArray(data.fields),
    } as unknown as IPackCreate);
  };

  useEffect(() => {
    const { service } = watch();

    const newService = fullServices.find((f) => f.id === service);
    if (newService) {
      setValue('name', newService.name);
    }
  }, [watch('service')]);

  useEffect(() => {
    const { server } = watch();
    loadPacksWithServer(server);
  }, []);

  useEffect(() => {
    const { server } = watch();
    loadPacksWithServer(server);
  }, [watch('server')]);

  const handleSetPack = (data: IServerPack) => {
    setValue('type', data.id);
    setValue('quantitymax', data.max ?? 0);
    setValue('quantitymin', data.min ?? 0);
    setValue('pricebuy', data.price ?? 0);
    setValue('template', data.config ?? '');
    setValue('serviceprovider', data.provider ?? '');
  };

  const handleSetType = (type: string) => {
    setValue('type', type);
    setValue('quantitymax', 0);
    setValue('quantitymin', 0);
    setValue('pricebuy', 0);
    setValue('template', '');
    setValue('serviceprovider', '');
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitForm)}>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <RHFSelect
          name="status"
          label="Trạng thái"
          size="small"
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
        >
          {PackStatus.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                mx: 1,
                my: 0.5,
                borderRadius: 0.75,
                typography: 'body2',
                textTransform: 'capitalize',
                '&:first-of-type': { mt: 0 },
                '&:last-of-type': { mb: 0 },
              }}
            >
              {option.name}
            </MenuItem>
          ))}
        </RHFSelect>
        <RHFSelect
          fullWidth
          name="server"
          label="server"
          size="small"
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
        >
          {fullServers
            .filter((f) => f.serverType !== TypeService.Via)
            .map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 0.75,
                  typography: 'body2',
                  textTransform: 'capitalize',
                  '&:first-of-type': { mt: 0 },
                  '&:last-of-type': { mb: 0 },
                }}
              >
                {option.displayname}
              </MenuItem>
            ))}
        </RHFSelect>
        <RHFSelect
          name="service"
          label="Dịch vụ"
          size="small"
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
        >
          {fullServices.map((option) => (
            <MenuItem
              key={option.id}
              value={option.id}
              sx={{
                mx: 1,
                my: 0.5,
                borderRadius: 0.75,
                typography: 'body2',
                textTransform: 'capitalize',
                '&:first-of-type': { mt: 0 },
                '&:last-of-type': { mb: 0 },
              }}
            >
              {`${option.group} - ${option.displayname}`}
            </MenuItem>
          ))}
        </RHFSelect>
        <RHFSelect
          name="name"
          label="Loại dịch vụ"
          size="small"
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: false }}
          disabled
        >
          {fullServices.map((option) => (
            <MenuItem
              key={option.name}
              value={option.name}
              sx={{
                mx: 1,
                my: 0.5,
                borderRadius: 0.75,
                typography: 'body2',
                textTransform: 'capitalize',
                '&:first-of-type': { mt: 0 },
                '&:last-of-type': { mb: 0 },
              }}
            >
              {option.name}
            </MenuItem>
          ))}
        </RHFSelect>
        <RHFTextField name="displayname" label="Tên hiển thị" size="small" />
        <PackProviderPopover
          text={watch('type')}
          onChangeText={handleSetType}
          packs={packs}
          onChangePack={handleSetPack}
        />
        <RHFTextField name="pricesale" type="number" label="Giá bán ra" size="small" />
        <RHFTextField name="pricebuy" label="Giá mua vào" type="number" size="small" />
        <RHFTextField name="quantitymin" label="SL Nhỏ nhất" type="number" size="small" />
        <RHFTextField name="quantitymax" label="SL Lớn nhất" type="number" size="small" />
        <RHFSwitch name="isuid" label="isuid" />
        <RHFTextField
          name="discount"
          label="% Chiết khấu (<30)"
          type="number"
          size="small"
          InputProps={{ inputProps: { min: 0, max: 30 } }}
        />
        <RHFTextField
          name="maxdiscount"
          label="Tối đa số tiền được giảm (Số Coin)"
          type="number"
          size="small"
          InputProps={{ inputProps: { min: 0 } }}
        />
        <RHFTextField name="fields" label="Fields" multiline minRows={3} size="small" />
        <RHFTextField name="actions" label="Action" multiline minRows={3} size="small" />
        <RHFJSONEditor name="template" label="Mẫu template body Data" />
        <RHFJSONEditor name="items" label="Cấu hình danh sách Field" />
        <RHFTextField
          name="serviceprovider"
          label="Cấu hình dịch vụ (cách nhau dấu ;)"
          size="small"
        />
        <RHFSelect
          name="flag"
          label="Quốc gia"
          size="small"
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: false }}
        >
          {countries.map((option) => (
            <MenuItem
              key={option.code}
              value={option.code}
              sx={{
                mx: 1,
                my: 0.5,
                borderRadius: 0.75,
                typography: 'body2',
                textTransform: 'capitalize',
                '&:first-of-type': { mt: 0 },
                '&:last-of-type': { mb: 0 },
              }}
            >
              {option.name}
            </MenuItem>
          ))}
        </RHFSelect>
      </Stack>
      <LoadingButton
        fullWidth
        color="inherit"
        size="small"
        type="submit"
        variant="contained"
        loading={isSubmitSuccessful || isSubmitting}
        sx={{
          bgcolor: 'text.primary',
          color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
          '&:hover': {
            bgcolor: 'text.primary',
            color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
          },
        }}
      >
        {isUpdate ? 'Cập nhật' : 'Tạo mới'}
      </LoadingButton>
    </FormProvider>
  );
}
